import React from 'react';

import {Box} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';

import {
    EfeAccordionValues, EfeCatalog,
    EfeResponse,
    Field,
    PersonalDataServiceRequest,
    ValidationsCURPResponse,
} from 'commons/models';
import {
    Constants,
    HistoryRoutes,
    Messages,
    PersonTypes,
    SnackbarVariants,
    ViewKeys,
} from 'commons/constants';
import {CatalogServices, ClientService, ValidationsService} from 'services';
import {EfeAccordion} from 'components';
import {MainContent} from 'layouts/commons';
import {useAlert, useFlowFieldSearch, useSpinner} from 'hooks';
import DocsInfoForm from './docs-info-form/DocsInfoForm';
import {formatDateToISO, nextStep} from 'commons/utils/Functions';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import ForeingClients from 'services/clients/ForeingClients';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';
import {useUserContext} from 'context/user/UserContext';
import {useCatalogContext} from '../../../context/onboarding-catalog/OnboardingCatalog';

function DocsInfo() {
    const history = useHistory();
    const {user} = useUserContext();
    const {flow, updateModuleStep} = useFlowContext();
    const {personType, menu, expedientId, fatherKycId, foreign} = useMenuContext();
    const [fields, setFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [personalData, setPersonalData] = React.useState<any>();
    const {enqueueSnackbar} = useSnackbar();
    const {catalogs} = useCatalogContext();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();
    const [renapoData, setRenapoData] = React.useState<ValidationsCURPResponse>();
    const [catBanks, setCatBanks] = React.useState<any>([]);

    React.useEffect(() => {
        if (fatherKycId > 0 && ViewKeys.DATA_DOCUMENTATION_INFO === flow.data.expandKey) {
            setIsLoading(true);
            (async function () {
                try {
                    let response: EfeResponse<PersonalDataServiceRequest>;
                    const responseBanks = await CatalogServices.getValuesFrom('CAT_BANCOS');
                    if (responseBanks && responseBanks.data && responseBanks.data.values) {
                        setCatBanks(responseBanks.data.values);
                    }
                    if (foreign) {
                        response = await ForeingClients.getPersonalData(fatherKycId);
                    } else {
                        response = await ClientService.getClientPersonalData(fatherKycId);
                        if (response && response.data && response.data.curp) {
                            const responseCurp: EfeResponse<ValidationsCURPResponse> =
                                await ValidationsService.validateCurp({
                                    curp: response.data?.curp,
                                    documento: 0,
                                });
                            setRenapoData(responseCurp.data);
                        }
                    }

                    if (response && response.data) {
                        loadEconomicFieldsValues(response);
                        setPersonalData(response.data);
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                }
            })();
        }
    }, []);

    const accordion: Array<EfeAccordionValues> = [
        {
            icon: <ActionIcon />,
            title: 'Datos Generales',
            reactElement: (
                <Box mt={8}>
                    {!isLoading && catBanks && (
                        <DocsInfoForm
                            rfc={user.username}
                            foreign={foreign}
                            fields={fields}
                            submitHandler={onSubmit}
                            personalData={personalData}
                            renapoData={renapoData}
                            catBanks={catBanks}
                        />
                    )}
                </Box>
            ),
            expandIcon: true,
            disabled: false,
            expanded: ViewKeys.DATA_DOCUMENTATION_INFO === flow.data.expandKey,
        },
    ];

    function loadEconomicFieldsValues(response: EfeResponse<PersonalDataServiceRequest>) {
        if (response && response.data && response.data.economicActivity) {
            response.data.economicActivityDetail = response.data.economicActivity;
            const catalog: EfeCatalog | undefined = catalogs.find(
                (catalog: EfeCatalog) => catalog.name === 'CAT_ACTIVIDADES_ECONOMICAS'
            );
            if (catalog) {
                const catalogResumeId = catalog.values.find(item => {
                    return item.id === response.data?.economicActivity;
                });
                if (catalogResumeId) {
                    response.data.economicActivity = catalogResumeId.secondaryId;
                }
            }
        }
    }

    async function onSubmit(values: PersonalDataServiceRequest) {
        const request = {...values};
        try {
            setIsLoading(true);
            request.personTypeKey = personType;
            request.viewFatherId = flow.data.fatherId;
            request.viewId = flow.data.viewId;
            request.expedientId = expedientId;
            request.kycId = fatherKycId;
            request.workflowId = 1;
            request.role = Constants.CLIENT;
            request.birthDate = formatDateToISO(values.birthDate);
            request.constitutionDate = formatDateToISO(values.constitutionDate);
            request.protocolizationDate = formatDateToISO(values.protocolizationDate);
            request.economicActivity = values.economicActivityDetail;
            let response: EfeResponse<PersonalDataServiceRequest>;
            if (foreign && personType !== PersonTypes.MORAL) {
                response = await ForeingClients.updatePersonalData(request);
            } else {
                response = await ClientService.updatePersonalData(request.personTypeKey, request);
            }

            if (response && response.data) {
                enqueueSnackbar(Messages.INFORMATION_SAVED_SUCCESSFULLY, {
                    variant: SnackbarVariants.SUCCESS,
                });
                const path = nextStep(flow, menu);
                if (Constants.MODULE_STEP !== path) {
                    setIsLoading(false);
                    history.push(path);
                } else {
                    updateModuleStep(true);
                    flow.data.expandKey = path;
                    setIsLoading(false);
                }
            } else {
                setFields([...fields]);
                setIsLoading(false);
            }
        } catch (error: any) {
            setIsLoading(false);
            setFields([...fields]);
            showAlertError(error, Messages.ERROR_SAVING_INFORMATION);
        }
    }

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}

            <EfeAccordion
                onChange={() => history.push(HistoryRoutes.DOCS_INFO)}
                values={accordion}
            />
        </MainContent>
    );
}

export default DocsInfo;
